/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  COUNTRY_CODES,
  FLOX_LOGO_BLUE_SRC,
  REGISTRATION_URL,
  WAREHOUSE_CLIENT,
  WAREHOUSE_MANAGER,
} from '@constants';
import { useDocumentTitle } from 'usehooks-ts';
import dayjs from 'dayjs';
import { isAxiosRegisterError } from '@utils';
import { AxiosResponse, AxiosError } from 'axios';
import { useAuthHttp } from '@hooks';
import { useAuth } from '@context';
import {
  getPasswordStrength,
  getPasswordFeedback,
} from '@components/PasswordStrengthBar';
import {
  ExclamationTriangleIcon,
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  PhoneIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  TruckIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { RegisterValues, RegisterResponse, RegisterErrorData } from './types';

const registerValidationSchema = Yup.object({
  role: Yup.string()
    .oneOf(['logistics-client', 'logistics-provider'])
    .required('Please select what service you need.'),
  businessName: Yup.string()
    .min(3, 'Company name must have at least 3 characters.')
    .required('A business name is required.'),
  industry: Yup.string()
    .min(3, 'Industry must have at least 3 characters.')
    .required('An industry is required.'),
  firstName: Yup.string()
    .min(3, 'First name must have at least 3 characters.')
    .required('A first name is required.'),
  lastName: Yup.string()
    .min(3, 'Last name must have at least 3 characters.')
    .required('A last name is required.'),
  country: Yup.string().required('Please select a country.'),
  mobilePhoneNumber: Yup.string()
    .matches(/^[0-9]{6,14}$/, {
      message: 'Please enter a valid mobile phone number',
    })
    .required('A mobile phone number is required.'),
  email: Yup.string()
    .email('Invalid email address.')
    .required('An email address is required.'),
  password: Yup.string()
    .min(8, 'Password must have at least 8 characters.')
    .required('A password is required.')
    .test({
      name: 'password-strength-test',
      test: (value, ctx) => {
        if (!(getPasswordStrength(value as string) > 1)) {
          return ctx.createError({
            message: getPasswordFeedback(value as string),
          });
        }
        return true;
      },
    }),
  hasAgreedToTandC: Yup.boolean().oneOf(
    [true],
    'You must accept the terms and conditions'
  ),
});

export function LegacyRegistration() {
  const navigate = useNavigate();
  const { logIn, setUser } = useAuth();
  const authHttp = useAuthHttp();
  const [searchParams] = useSearchParams();
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showRegisterError, setShowRegisterError] = useState(false);
  const [registerErrorMessage, setRegisterErrorMessage] = useState('');
  const [showLoginLink, setShowLoginLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useDocumentTitle('FLOX - Register');

  const role = searchParams.get('role') || '';
  const redirect = searchParams.get('redirect') || '';

  let redirectWithParams = '';

  const maxStorageCapacityMetersParam =
    searchParams.get('maxStorageCapacityMeters') || '';
  const maxStorageCapacityPalletsParam =
    searchParams.get('maxStorageCapacityPallets') || '';
  const ownFleetParam = searchParams.get('ownFleet') || '';
  const temperatureControlledParam =
    searchParams.get('temperatureControlled') || '';
  const warehouseParam = searchParams.get('warehouse') || '';
  const distributionParam = searchParams.get('distribution') || '';
  const availableStorageCapacityMetersParam =
    searchParams.get('availableStorageCapacityMeters') || '';
  const availableStorageCapacityPalletsParam =
    searchParams.get('availableStorageCapacityPallets') || '';
  const minServiceTermMonthsParam =
    searchParams.get('minServiceTermMonths') || '';
  const minServiceCapacityParam = searchParams.get('minServiceCapacity') || '';

  let businessProfile = {};

  const servicesNeeded = searchParams.get('servicesNeeded') || '';
  const storageSpaceOrMinPalletsNeeded =
    searchParams.get('storageSpaceOrMinPalletsNeeded') || '';
  const intendedStartDate = searchParams.get('intendedStartDate') || '';
  const minimalTerm = searchParams.get('minimalTerm') || '';
  const transportationNeeded = searchParams.get('transportationNeeded') || 0;
  const averageShipmentType = searchParams.get('averageShipmentType') || '';
  const averageShipmentSize = searchParams.get('averageShipmentSize') || 0;
  const needed = searchParams.get('needed') || [];
  const additionalCapabilities =
    searchParams.get('additionalCapabilities') || [];
  const sizeOfBusiness = searchParams.get('sizeOfBusiness') || '';
  const typeOfBusiness = searchParams.get('typeOfBusiness') || '';
  const supplyChainTeam = searchParams.get('supplyChainTeam') || '';

  if (role === 'logistics-client') {
    businessProfile = {
      businessType: typeOfBusiness,
      businessSize: sizeOfBusiness,
      numberOfPalletsInAndOutPerWeek: transportationNeeded,
      averageShipmentType,
      averageShipmentSizePerWeek: averageShipmentSize,
      teamSize: supplyChainTeam,
    };
  }

  if (role === 'logistics-provider') {
    businessProfile = {
      businessType: searchParams.get('typeOfBusiness') || '',
      businessSize: searchParams.get('sizeOfBusiness') || '',
      targetCustomerType: searchParams.get('targetCustomerType') || '',
    };
  }

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<RegisterValues>({
    resolver: yupResolver(registerValidationSchema),
    mode: 'onChange',
    defaultValues: {
      role: searchParams.get('role') || '',
      businessName: '',
      industry: '',
      firstName: searchParams.get('firstname') || searchParams.get('name')?.split(' ')[0] || '',
      lastName: searchParams.get('lastname') || searchParams.get('name')?.split(' ').slice(1).join(' ') || '',
      mobilePhoneNumber: '',
      email: searchParams.get('email') || '',
      password: '',
      hasAgreedToTandC: false,
      country: 'GB',
    },
  });

  const watchedPassword = watch('password');

  const onSubmit = async (values: RegisterValues): Promise<void> => {
    setRegisterErrorMessage('');
    setShowRegisterError(false);

    if (role === 'logistics-client') {
      const redirectParams = `servicesNeeded=${servicesNeeded}&storageSpaceOrMinPalletsNeeded=${storageSpaceOrMinPalletsNeeded}&intendedStartDate=${intendedStartDate}&minimalTerm=${minimalTerm}&transportationNeeded=${transportationNeeded}&averageShipmentType=${averageShipmentType}&averageShipmentSize=${averageShipmentSize}&needed=${needed}&additionalCapabilities=${additionalCapabilities}&sizeOfBusiness=${sizeOfBusiness}&typeOfBusiness=${typeOfBusiness}&supplyChainTeam=${supplyChainTeam}&role=${role}`;
      const separator = redirect.includes('?') ? '&' : '?';
      redirectWithParams = `${redirect}${separator}${redirectParams}`;
    }

    if (role === 'logistics-provider') {
      const redirectParams = `maxStorageCapacityMeters=${maxStorageCapacityMetersParam}&maxStorageCapacityPallets=${maxStorageCapacityPalletsParam}&ownFleet=${ownFleetParam}&temperatureControlled=${temperatureControlledParam}&warehouse=${warehouseParam}&distribution=${distributionParam}&availableStorageCapacityMeters=${availableStorageCapacityMetersParam}&availableStorageCapacityPallets=${availableStorageCapacityPalletsParam}&minServiceTermMonths=${minServiceTermMonthsParam}&minServiceCapacity=${minServiceCapacityParam}&name=${values.firstName}%20${values.lastName}&email=${values.email}&phone=${values.mobilePhoneNumber}`;
      const separator = redirect.includes('?') ? '&' : '?';
      redirectWithParams = `${redirect}${separator}${redirectParams}`;
    }

    await authHttp
      .post(REGISTRATION_URL, {
        role:
          values.role === 'logistics-client'
            ? WAREHOUSE_CLIENT
            : WAREHOUSE_MANAGER,
        businessName: values.businessName,
        firstName: values.firstName,
        lastName: values.lastName,
        mobilePhoneNumber: `+${
          COUNTRY_CODES.find(
            (country) => country.country_code === values.country
          )?.phone_code
        }${values.mobilePhoneNumber}`,
        email: values.email,
        password: values.password,
        hasAgreedToTandC: values.hasAgreedToTandC,
        isSkippingRegistrationEmail: false,
        baseUrl: null,
        businessProfile: { ...businessProfile, industry: values.industry },
      })
      .then(async ({ data }: AxiosResponse<RegisterResponse>) => {
        if (process.env.NODE_ENV === 'development') {
          setUser(data.registrationResult.user);
          logIn(
            data.registrationResult.user,
            data.auth,
            redirectWithParams || redirect || '/noticeboard'
          );
        } else {
          try {
            const response = await fetch(
              'https://api.hsforms.com/submissions/v3/integration/submit/3810753/fd613e6a-cac7-450e-941e-2c05c8c69bc0',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  submittedAt: dayjs().valueOf(),
                  fields: [
                    {
                      objectTypeId: '0-1',
                      name: 'email',
                      value: values.email,
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'firstname',
                      value: values.firstName,
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'lastname',
                      value: values.lastName,
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'phone',
                      value: `+${
                        COUNTRY_CODES.find(
                          (country) => country.country_code === values.country
                        )?.phone_code
                      }${values.mobilePhoneNumber}`,
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'company',
                      value: values.businessName,
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'client_type',
                      value:
                        values.role === 'logistics-client'
                          ? 'Buyer (Shipper)'
                          : 'Seller (LSP)',
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'funnel_stage',
                      value: 'Account created',
                    },
                    {
                      objectTypeId: '0-1',
                      name: 'lead_source',
                      value: 'FLOX Registration',
                    },
                  ],
                }),
              }
            );

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          } catch (error) {
            console.error('There was an error!', error);
          }
          setUser(data.registrationResult.user);
          logIn(
            data.registrationResult.user,
            data.auth,
            redirectWithParams || redirect || '/noticeboard'
          );
        }
      })
      .catch((err: AxiosError<RegisterErrorData>) => {
        const errors = err.response?.data?.registrationResult?.errors;
        const errorMessage = errors?.[0];

        // Reset states
        setShowLoginLink(false);

        // 423: Redirect to pending approval page
        if (err.response?.status === 423) {
          navigate('/pending-approval');
          return;
        }

        // 409: User already exists - show login link
        if (err.response?.status === 409) {
          setRegisterErrorMessage(
            errorMessage ||
              'An account with this email already exists. Please log in instead.'
          );
          setShowLoginLink(true);
          setShowRegisterError(true);
          return;
        }

        // Use backend error message if available
        if (errorMessage) {
          setRegisterErrorMessage(errorMessage);
          setShowRegisterError(true);
          return;
        }

        // Fallback error handling
        if (isAxiosRegisterError(err)) {
          let registerErrors = err.response?.data?.registrationResult?.errors;
          if (!registerErrors) {
            registerErrors =
              (err.response?.data as any | undefined)?.errors.map(
                (x: any) => x.errorMessage
              ) ?? [];
          }
          const errorMessages = registerErrors?.map((message: string) =>
            message.replace('[', '').replace(']', '')
          ) as string[];

          if (err?.response?.status === 400) {
            setRegisterErrorMessage(
              errorMessages[0] || 'Registration failed. Please check your details.'
            );
          } else {
            setRegisterErrorMessage('Something went wrong. Please try again later.');
          }
        } else {
          setRegisterErrorMessage('Something went wrong. Please try again later.');
        }

        setShowRegisterError(true);
      });
  };

  const getPasswordStrengthColor = (score: number) => {
    switch (score) {
      case 0:
        return 'bg-gray-200';
      case 1:
        return 'bg-red-500';
      case 2:
        return 'bg-yellow-500';
      case 3:
        return 'bg-blue-500';
      case 4:
        return 'bg-green-500';
      default:
        return 'bg-gray-200';
    }
  };

  const getPasswordStrengthText = (score: number) => {
    switch (score) {
      case 0:
        return '';
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Strong';
      default:
        return '';
    }
  };

  const getPasswordStrengthTextColor = (score: number) => {
    switch (score) {
      case 0:
      case 1:
        return 'text-red-600';
      case 2:
        return 'text-yellow-600';
      case 3:
        return 'text-blue-600';
      case 4:
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const inputClassName = (hasError: boolean) =>
    `block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
      hasError
        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
        : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
    } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`;

  return (
    <main className='flex min-h-screen w-full'>
      {/* Image Panel */}
      <section
        className='hidden lg:block lg:w-1/2 min-h-screen bg-cover bg-no-repeat bg-center transition-all duration-300'
        style={{ backgroundImage: 'url(/images/bg-register.png)' }}
      />

      {/* Form Panel */}
      <section className='flex-1 flex items-center justify-center bg-white px-4 py-12 overflow-y-auto'>
        <div className='w-full max-w-lg'>
          {/* Logo */}
              <div className='flex justify-center mb-6'>
                <img
                  src={FLOX_LOGO_BLUE_SRC}
                  alt='Flox logo'
                  className='h-14 w-auto'
                />
              </div>

              {/* Title */}
              <div className='text-center mb-6'>
                <h1 className='text-3xl font-bold text-gray-900 mb-2'>
                  Create an account
                </h1>
                <p className='text-gray-500'>Join the FLOX Platform</p>
              </div>

              {/* Registration Card */}
              <div className='bg-white rounded-2xl shadow-lg border border-gray-100 p-8'>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  {/* Role Selection - only show if not pre-selected */}
                  {!role && (
                    <div className='mb-6'>
                      <label className='block text-sm font-medium text-gray-700 mb-3'>
                        I am a...
                      </label>
                      <Controller
                        name='role'
                        control={control}
                        render={({ field }) => (
                          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                            <button
                              type='button'
                              onClick={() => field.onChange('logistics-client')}
                              disabled={isSubmitting}
                              className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                                field.value === 'logistics-client'
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <CubeIcon
                                className={`h-8 w-8 mb-2 ${
                                  field.value === 'logistics-client'
                                    ? 'text-blue-600'
                                    : 'text-gray-400'
                                }`}
                              />
                              <span
                                className={`text-sm font-medium ${
                                  field.value === 'logistics-client'
                                    ? 'text-blue-700'
                                    : 'text-gray-700'
                                }`}
                              >
                                I need logistics services
                              </span>
                            </button>
                            <button
                              type='button'
                              onClick={() =>
                                field.onChange('logistics-provider')
                              }
                              disabled={isSubmitting}
                              className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                                field.value === 'logistics-provider'
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <TruckIcon
                                className={`h-8 w-8 mb-2 ${
                                  field.value === 'logistics-provider'
                                    ? 'text-blue-600'
                                    : 'text-gray-400'
                                }`}
                              />
                              <span
                                className={`text-sm font-medium ${
                                  field.value === 'logistics-provider'
                                    ? 'text-blue-700'
                                    : 'text-gray-700'
                                }`}
                              >
                                I am a Logistics Provider
                              </span>
                            </button>
                          </div>
                        )}
                      />
                      {errors.role && (
                        <p className='mt-2 text-sm text-red-600'>
                          {errors.role.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Business Name */}
                  <div className='mb-4'>
                    <label
                      htmlFor='businessName'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Business name
                    </label>
                    <Controller
                      name='businessName'
                      control={control}
                      render={({ field }) => (
                        <div className='relative'>
                          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                            <BuildingOfficeIcon className='h-5 w-5 text-gray-400' />
                          </div>
                          <input
                            {...field}
                            type='text'
                            id='businessName'
                            placeholder='Your company name'
                            disabled={isSubmitting}
                            className={inputClassName(!!errors.businessName)}
                          />
                        </div>
                      )}
                    />
                    {errors.businessName && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.businessName.message}
                      </p>
                    )}
                  </div>

                  {/* Industry */}
                  <div className='mb-4'>
                    <label
                      htmlFor='industry'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Industry
                    </label>
                    <Controller
                      name='industry'
                      control={control}
                      render={({ field }) => (
                        <div className='relative'>
                          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                            <BriefcaseIcon className='h-5 w-5 text-gray-400' />
                          </div>
                          <input
                            {...field}
                            type='text'
                            id='industry'
                            placeholder='Your industry'
                            disabled={isSubmitting}
                            className={inputClassName(!!errors.industry)}
                          />
                        </div>
                      )}
                    />
                    {errors.industry && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.industry.message}
                      </p>
                    )}
                  </div>

                  {/* Name Fields - Side by Side */}
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4'>
                    {/* First Name */}
                    <div>
                      <label
                        htmlFor='firstName'
                        className='block text-sm font-medium text-gray-700 mb-2'
                      >
                        First name
                      </label>
                      <Controller
                        name='firstName'
                        control={control}
                        render={({ field }) => (
                          <div className='relative'>
                            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                              <UserIcon className='h-5 w-5 text-gray-400' />
                            </div>
                            <input
                              {...field}
                              type='text'
                              id='firstName'
                              placeholder='First name'
                              autoComplete='given-name'
                              disabled={isSubmitting}
                              className={inputClassName(!!errors.firstName)}
                            />
                          </div>
                        )}
                      />
                      {errors.firstName && (
                        <p className='mt-1.5 text-sm text-red-600'>
                          {errors.firstName.message}
                        </p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label
                        htmlFor='lastName'
                        className='block text-sm font-medium text-gray-700 mb-2'
                      >
                        Last name
                      </label>
                      <Controller
                        name='lastName'
                        control={control}
                        render={({ field }) => (
                          <div className='relative'>
                            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                              <UserIcon className='h-5 w-5 text-gray-400' />
                            </div>
                            <input
                              {...field}
                              type='text'
                              id='lastName'
                              placeholder='Last name'
                              autoComplete='family-name'
                              disabled={isSubmitting}
                              className={inputClassName(!!errors.lastName)}
                            />
                          </div>
                        )}
                      />
                      {errors.lastName && (
                        <p className='mt-1.5 text-sm text-red-600'>
                          {errors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className='mb-4'>
                    <label
                      htmlFor='mobilePhoneNumber'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Mobile phone number
                    </label>
                    <div className='relative flex'>
                      <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-10'>
                        <PhoneIcon className='h-5 w-5 text-gray-400' />
                      </div>
                      <Controller
                        name='country'
                        control={control}
                        render={({ field }) => (
                          <select
                            {...field}
                            aria-label='Country'
                            disabled={isSubmitting}
                            className='rounded-l-lg border border-r-0 border-gray-200 bg-gray-50 py-3 pl-10 pr-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed'
                          >
                            {COUNTRY_CODES.map((country) => (
                              <option
                                value={country.country_code}
                                key={country.country_code}
                              >
                                {country.country_code} +{country.phone_code}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                      <Controller
                        name='mobilePhoneNumber'
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type='tel'
                            id='mobilePhoneNumber'
                            placeholder='Phone number'
                            autoComplete='tel'
                            disabled={isSubmitting}
                            className={`flex-1 rounded-r-lg border py-3 px-3 text-sm transition-colors ${
                              errors.mobilePhoneNumber
                                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                            } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                          />
                        )}
                      />
                    </div>
                    {errors.mobilePhoneNumber && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.mobilePhoneNumber.message}
                      </p>
                    )}
                    {errors.country && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.country.message}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div className='mb-4'>
                    <label
                      htmlFor='email'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Email address
                    </label>
                    <Controller
                      name='email'
                      control={control}
                      render={({ field }) => (
                        <div className='relative'>
                          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                            <EnvelopeIcon className='h-5 w-5 text-gray-400' />
                          </div>
                          <input
                            {...field}
                            type='email'
                            id='email'
                            placeholder='you@company.com'
                            autoComplete='email'
                            disabled={isSubmitting}
                            className={inputClassName(!!errors.email)}
                          />
                        </div>
                      )}
                    />
                    {errors.email && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className='mb-4'>
                    <label
                      htmlFor='password'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Password
                    </label>
                    <Controller
                      name='password'
                      control={control}
                      render={({ field }) => (
                        <div className='relative'>
                          <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                            <LockClosedIcon className='h-5 w-5 text-gray-400' />
                          </div>
                          <input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            id='password'
                            placeholder='Create a password'
                            autoComplete='new-password'
                            disabled={isSubmitting}
                            className={inputClassName(!!errors.password).replace('pr-3', 'pr-10')}
                            onChange={(e) => {
                              field.onChange(e);
                              setPasswordStrength(
                                getPasswordStrength(e.target.value)
                              );
                            }}
                            onFocus={() => setPasswordFocus(true)}
                            onBlur={() => setPasswordFocus(false)}
                          />
                          <button
                            type='button'
                            onClick={() => setShowPassword(!showPassword)}
                            className='absolute inset-y-0 right-0 flex items-center pr-3'
                          >
                            {showPassword
                              ? <EyeSlashIcon className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                              : <EyeIcon className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                            }
                          </button>
                        </div>
                      )}
                    />
                    <p className='mt-1.5 text-xs text-gray-500'>
                      8 characters minimum, including a number
                    </p>
                    {/* Password Strength Bar */}
                    {(passwordFocus || watchedPassword) && (
                      <div className='mt-2'>
                        <div className='flex gap-1'>
                          {[0, 1, 2, 3].map((index) => (
                            <div
                              key={index}
                              className={`h-1 flex-1 rounded-full transition-colors ${
                                index < passwordStrength
                                  ? getPasswordStrengthColor(passwordStrength)
                                  : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        {passwordStrength > 0 && (
                          <p
                            className={`text-xs mt-1 ${getPasswordStrengthTextColor(
                              passwordStrength
                            )}`}
                          >
                            {getPasswordStrengthText(passwordStrength)}
                          </p>
                        )}
                      </div>
                    )}
                    {errors.password && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  {/* Terms and Conditions */}
                  <div className='mb-6'>
                    <Controller
                      name='hasAgreedToTandC'
                      control={control}
                      render={({ field }) => (
                        <label className='flex items-start gap-3 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={isSubmitting}
                            className='mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50'
                          />
                          <span className='text-sm text-gray-600'>
                            I agree to the{' '}
                            <a
                              href='https://www.flox.is/platform-terms'
                              target='_blank'
                              rel='noreferrer'
                              className='text-blue-600 hover:text-blue-800 hover:underline'
                            >
                              Terms and Conditions
                            </a>
                          </span>
                        </label>
                      )}
                    />
                    {errors.hasAgreedToTandC && (
                      <p className='mt-1.5 text-sm text-red-600'>
                        {errors.hasAgreedToTandC.message}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type='submit'
                    intent='primary'
                    size='lg'
                    disabled={!isValid || isSubmitting}
                    className='w-full'
                    startIcon={
                      isSubmitting ? (
                        <FloxSpinner size='sm' />
                      ) : undefined
                    }
                    label={isSubmitting ? 'Creating account...' : 'Create account'}
                  />

                  {/* Error Message - shown below button for visibility */}
                  {showRegisterError && (
                    <div className='rounded-lg bg-yellow-50 p-4 mt-4'>
                      <div className='flex'>
                        <ExclamationTriangleIcon className='h-5 w-5 text-yellow-400 flex-shrink-0' />
                        <div className='ml-3'>
                          <p className='text-sm text-yellow-700'>
                            {registerErrorMessage}
                          </p>
                          {showLoginLink && (
                            <button
                              type='button'
                              onClick={() => navigate('/login')}
                              className='text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline mt-2'
                            >
                              Go to login page
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Secured By */}
                  <div className='flex items-center justify-center gap-2 mt-6 text-gray-400'>
                    <ShieldCheckIcon className='h-4 w-4' />
                    <span className='text-xs'>
                      Secured by FLOX Enterprise Authentication
                    </span>
                  </div>
                </form>
              </div>

              {/* Footer Links */}
              <div className='mt-8 text-center space-y-4'>
                <div className='flex justify-center items-center gap-2'>
                  <span className='text-sm text-gray-500'>
                    Already have an account?
                  </span>
                  <button
                    type='button'
                    onClick={() => {
                      reset();
                      navigate('/login');
                    }}
                    className='text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline'
                  >
                    Log in
                  </button>
                </div>

                <p className='text-sm text-gray-400 pt-4'>
                  Need help? Contact{' '}
                  <a
                    href='mailto:support@flox.is'
                    className='text-blue-600 hover:underline'
                  >
                    support@flox.is
                  </a>
                </p>
              </div>
        </div>
      </section>
    </main>
  );
}
